<script setup lang="ts">
import { computed, onMounted, ref } from "vue"

import dayjs from "dayjs"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import Table from "@/components/shared/Table.vue"
import purchaseProductService from "@/services/purchase-product"
import type { PurchaseProduct } from "@/types/purchase-product.type"
import { reportSentry } from "@/utils/sentry"
import { formattedDate, unitedNumber } from "@/utils/string"

const productsToShipOut = ref<PurchaseProduct[]>([])
const productsInTransit = ref<PurchaseProduct[]>([])

const endOfToday = computed(() => {
  return dayjs().endOf("day").toDate()
})

// fetch function
async function loadProducts() {
  productsToShipOut.value = await purchaseProductService.getConfirmedList()
  productsInTransit.value = await purchaseProductService.getDeliveryRequestedList()
}

// initial load
onMounted(async () => {
  try {
    await loadProducts()
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
})
</script>

<template>
  <LayoutAuthed :title="$t('nav.shipOuts')">
    <Card>
      <CardTitle>{{ $t("shipOut.description") }} ({{ productsToShipOut.length }})</CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("order.title") }}</th>
              <th>{{ $t("item.code") }}</th>
              <th>{{ $t("item.title") }}</th>
              <th>{{ $t("colorProduct.colorCode") }}</th>
              <th>{{ $t("order.quantity") }}</th>
              <th>{{ $t("order.deliveryMethod") }}</th>
              <th>{{ $t("order.pickupAtDue") }}</th>
              <th>{{ $t("shipOut.shippingMark") }}</th>
              <th>{{ $t("shipOut.title") }}</th>
            </tr>
          </template>
          <template #default>
            <tr
              v-for="(product, index) in productsToShipOut"
              :key="index"
              :class="{ 'scheduled-today': product.pickupAt && product.pickupAt.getTime() <= endOfToday.getTime() }"
            >
              <td>
                <router-link :to="`/orders/${product.purchase.id}`">{{ product.purchase.code }}</router-link>
              </td>
              <td>{{ product.product.quality.code }}</td>
              <td>{{ product.product.quality.name }}</td>
              <td>
                <span v-if="product.salesType === 'standard_order'">{{ product.product.colorCode }}</span>
                <span v-if="product.salesType === 'print'">{{ $t("orderItem.salesTypeList.print") }}</span>
              </td>
              <td>{{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}</td>
              <td>{{ $t(`order.deliveryMethodList.${product.deliveryMethod}`) }}</td>
              <td>{{ formattedDate(product.pickupAt) }}</td>
              <td>
                <Button
                  type="link"
                  size="sm"
                  color="warning"
                  :text="$t('shipOut.actions.printShippingMark')"
                  :path="`/ship_outs/${product.id}/set_up_mark`"
                />
              </td>
              <td>
                <Button
                  v-if="product.deliveryMethod !== 'quick'"
                  type="link"
                  size="sm"
                  color="success"
                  :text="$t('shipOut.actions.completeShipOut')"
                  :path="`/ship_outs/${product.purchase.id}/complete_order`"
                />
              </td>
            </tr>
          </template>
        </Table>
      </CardContent>
    </Card>
    <Card v-if="productsInTransit.length > 0">
      <CardTitle>{{ $t("shipOut.inTransit") }} ({{ productsInTransit.length }})</CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("order.title") }}</th>
              <th>{{ $t("item.title") }}</th>
              <th>{{ $t("colorProduct.colorCode") }}</th>
              <th>{{ $t("order.quantity") }}</th>
              <th>{{ $t("order.deliveryMethod") }}</th>
              <th>{{ $t("shipOut.shipper") }}</th>
              <th>{{ $t("shipOut.trackingCode") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(product, index) in productsInTransit" :key="index">
              <td>
                <router-link :to="`/orders/${product.purchase.id}`">{{ product.purchase.code }}</router-link>
              </td>
              <td>{{ product.product.quality.name }}</td>
              <td>
                <span v-if="product.salesType === 'standard_order'">{{ product.product.colorCode }}</span>
                <span v-if="product.salesType === 'print'">{{ $t("orderItem.salesTypeList.print") }}</span>
              </td>
              <td>{{ unitedNumber(product.quantity, $t("item.units.yards_short")) }}</td>
              <td>{{ $t(`order.deliveryMethodList.${product.deliveryMethod}`) }}</td>
              <td>{{ product.logisticsCompany || "-" }}</td>
              <td>{{ product.deliveryTrackingInfo || "-" }}</td>
            </tr>
          </template>
        </Table>
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped>
tr.scheduled-today {
  td {
    background-color: var(--bs-danger-bg-subtle);
  }
}
</style>
